// 양력 ↔ 음력 변환 (1901~2100, 한국 표준시). 서버 LunarCalendar.java 를 그대로 옮긴 것이다.
// 삭(朔)과 태양 황경으로 월 시작일·중기를 구하고, 동지가 든 달을 11월로 삼는다. 중기가 없는 달은 윤달.
import { julianDay, solveTerm, jdFromKst, kstFromJd } from './calc.js';

const rad = (d) => (d * Math.PI) / 180;
const sin = (d) => Math.sin(rad(d));

function deltaT(year) {
  const t = [[1900,-3],[1920,21],[1940,24],[1960,33],[1980,51],[2000,64],[2020,70],[2050,93],[2100,200]];
  if (year <= t[0][0]) return t[0][1];
  for (let i = 1; i < t.length; i++) {
    if (year <= t[i][0]) return t[i-1][1] + (t[i][1] - t[i-1][1]) * (year - t[i-1][0]) / (t[i][0] - t[i-1][0]);
  }
  return t[t.length - 1][1];
}

const A = [[299.77,0.107408,0.000325],[251.88,0.016321,0.000165],[251.83,26.651886,0.000164],
  [349.42,36.412478,0.000126],[84.66,18.206239,0.000110],[141.74,53.303771,0.000062],
  [207.14,2.453732,0.000060],[154.84,7.306860,0.000056],[34.52,27.261239,0.000047],
  [207.19,0.121824,0.000042],[291.34,1.844379,0.000040],[161.72,24.198154,0.000037],
  [239.56,25.513099,0.000035],[331.55,3.592518,0.000023]];

/** k번째 삭의 시각(JD, UT). Meeus ch.49 */
function newMoonJdUt(k) {
  const T = k / 1236.85, T2 = T * T, T3 = T2 * T, T4 = T3 * T;
  const jde = 2451550.09766 + 29.530588861 * k + 0.00015437 * T2 - 0.00000015 * T3 + 0.00000000073 * T4;
  const E = 1 - 0.002516 * T - 0.0000074 * T2;
  const M = 2.5534 + 29.10535670 * k - 0.0000014 * T2 - 0.00000011 * T3;
  const Mp = 201.5643 + 385.81693528 * k + 0.0107582 * T2 + 0.00001238 * T3 - 0.000000058 * T4;
  const F = 160.7108 + 390.67050284 * k - 0.0016118 * T2 - 0.00000227 * T3 + 0.000000011 * T4;
  const Om = 124.7746 - 1.56375588 * k + 0.0020672 * T2 + 0.00000215 * T3;
  let c = -0.40720 * sin(Mp) + 0.17241 * E * sin(M) + 0.01608 * sin(2 * Mp)
    + 0.01039 * sin(2 * F) + 0.00739 * E * sin(Mp - M) - 0.00514 * E * sin(Mp + M)
    + 0.00208 * E * E * sin(2 * M) - 0.00111 * sin(Mp - 2 * F) - 0.00057 * sin(Mp + 2 * F)
    + 0.00056 * E * sin(2 * Mp + M) - 0.00042 * sin(3 * Mp) + 0.00042 * E * sin(M + 2 * F)
    + 0.00038 * E * sin(M - 2 * F) - 0.00024 * E * sin(2 * Mp - M) - 0.00017 * sin(Om)
    - 0.00007 * sin(Mp + 2 * M) + 0.00004 * sin(2 * Mp - 2 * F) + 0.00004 * sin(3 * M)
    + 0.00003 * sin(Mp + M - 2 * F) + 0.00003 * sin(2 * Mp + 2 * F) - 0.00003 * sin(Mp + M + 2 * F)
    + 0.00003 * sin(Mp - M + 2 * F) - 0.00002 * sin(Mp - M - 2 * F) - 0.00002 * sin(3 * Mp + M)
    + 0.00002 * sin(4 * Mp);
  for (let i = 0; i < A.length; i++) {
    const a = A[i][0] + A[i][1] * k - (i === 0 ? 0.009173 * T2 : 0);
    c += A[i][2] * sin(a);
  }
  const year = 2000 + (jde - 2451545) / 365.25;
  return jde + c - deltaT(year) / 86400;
}

const dayNo = (jdUt) => Math.floor(jdUt + 9 / 24 + 0.5);
const dayNoOf = (y, m, d) => Math.floor(julianDay(y, m, d, 12) + 0.5);
const termDay = (lon, guessJd) => dayNo(solveTerm(lon, guessJd));
const winterSolsticeDay = (year) => termDay(270, jdFromKst(year, 12, 22, 12, 0));

function newMoonOnOrBefore(day) {
  let k = Math.round((day - 2451550.0) / 29.530588861) + 2;
  while (dayNo(newMoonJdUt(k)) > day) k--;
  while (dayNo(newMoonJdUt(k + 1)) <= day) k++;
  return dayNo(newMoonJdUt(k));
}
function nextNewMoon(startDay) {
  let k = Math.round((startDay - 2451550.0) / 29.530588861);
  while (dayNo(newMoonJdUt(k)) < startDay) k++;
  while (dayNo(newMoonJdUt(k - 1)) >= startDay) k--;
  return dayNo(newMoonJdUt(k + 1));
}

/** 음력 y년: 전년 동지월(11월)부터 올해 동지월 직전까지의 달들 */
function build(y) {
  const m11a = newMoonOnOrBefore(winterSolsticeDay(y - 1));
  const m11b = newMoonOnOrBefore(winterSolsticeDay(y));
  const starts = [];
  for (let s = m11a; s < m11b; s = nextNewMoon(s)) starts.push(s);
  starts.push(m11b);
  const n = starts.length - 1;
  const label = new Array(n), leap = new Array(n).fill(false);
  let leapIdx = -1;
  if (n === 13) {
    const wsJd = solveTerm(270, jdFromKst(y - 1, 12, 22, 12, 0));
    const jungi = [];
    for (let j = 0; j < 13; j++) jungi.push(termDay((270 + 30 * j) % 360, wsJd + 30.44 * j));
    for (let i = 1; i < n && leapIdx < 0; i++) {
      const has = jungi.some((jg) => jg >= starts[i] && jg < starts[i + 1]);
      if (!has) leapIdx = i;
    }
  }
  let cur = 11;
  for (let i = 0; i < n; i++) {
    if (i === leapIdx) { label[i] = cur; leap[i] = true; continue; }
    if (i > 0) cur = (cur % 12) + 1;
    label[i] = cur;
  }
  return { starts, label, leap };
}

export function toLunar(y, m, d) {
  const D = dayNoOf(y, m, d);
  for (const yy of [y, y + 1, y - 1]) {
    const Y = build(yy), n = Y.label.length;
    if (D >= Y.starts[0] && D < Y.starts[n]) {
      let i = n - 1;
      while (Y.starts[i] > D) i--;
      const ly = Y.label[i] >= 11 ? yy - 1 : yy;
      return { year: ly, month: Y.label[i], leap: Y.leap[i], day: D - Y.starts[i] + 1 };
    }
  }
  throw new Error('변환할 수 없는 날짜입니다');
}

/** 존재하지 않는 음력 날짜는 Error(message 를 화면에 그대로 보여 준다) */
export function toSolar(ly, lm, leap, ld) {
  const Y = build(lm >= 11 ? ly + 1 : ly), n = Y.label.length;
  for (let i = 0; i < n; i++) {
    if (Y.label[i] === lm && Y.leap[i] === leap) {
      const len = Y.starts[i + 1] - Y.starts[i];
      if (ld < 1 || ld > len) throw new Error(`그 달은 ${len}일까지 있어요`);
      const k = kstFromJd(Y.starts[i] + ld - 1 - 9 / 24);
      return { year: k[0], month: k[1], day: k[2] };
    }
  }
  throw new Error(leap ? `${ly}년에는 윤${lm}월이 없어요` : '존재하지 않는 음력 날짜예요');
}

export function hasLeap(ly, lm) {
  try { toSolar(ly, lm, true, 1); return true; } catch { return false; }
}
